import { useEffect, useMemo, useState } from 'react';
import { CustomFieldValue, Provider } from '../types';
import { getProviderById } from '../services/providers/getProviderById';
import { isPhysician } from '../utils/customFieldUtils';

export const useSupervisionRoleLabel = (provider: Provider | undefined) => {
  const [linkedProviders, setLinkedProviders] = useState<Provider[] | null>(
    null
  );

  const supervisionValues = provider?.customFields?.[
    'Supervision-Physician'
  ] as CustomFieldValue[] | undefined;

  const providerIds = useMemo(() => {
    if (
      !supervisionValues ||
      !Array.isArray(supervisionValues) ||
      supervisionValues.length === 0
    ) {
      return [] as string[];
    }

    const first = supervisionValues[0];
    const meta = (first?.metadata || {}) as {
      providers?: { providerId: string; providerLabel: string }[];
    };

    const ids =
      meta.providers?.map((p) => p.providerId).filter(Boolean) ?? [];

    return Array.from(new Set(ids));
  }, [supervisionValues]);

  useEffect(() => {
    let cancelled = false;

    const load = async () => {
      if (providerIds.length === 0) {
        setLinkedProviders([]);
        return;
      }

      try {
        const results: Provider[] = [];

        for (const id of providerIds) {
          const { data } = await getProviderById(id);
          const linked = data?.provider as Provider | undefined;
          if (linked) {
            results.push(linked);
          }
        }

        if (!cancelled) {
          setLinkedProviders(results);
        }
      } catch {
        if (!cancelled) {
          setLinkedProviders([]);
        }
      }
    };

    load();

    return () => {
      cancelled = true;
    };
  }, [providerIds]);

  const label = useMemo(() => {
    if (!linkedProviders || linkedProviders.length === 0) {
      // Neutral / legacy default when nothing is configured
      return 'Supervising Physician';
    }

    const anyPhysician = linkedProviders.some((p) => isPhysician(p));

    // If linked providers are physicians, this field is "Supervising Physician"
    // If they are midlevels (no MD/DO credentials), this field is "Supervises Midlevels"
    return anyPhysician ? 'Supervising Physician' : 'Supervises Midlevels';
  }, [linkedProviders]);

  return label;
};

